<script setup lang="ts">
/**
 * Health Check: "N content files failed to download or verify" opened in place — which items, why each one failed,
 * and where to go — instead of the player's whole error log (where unrelated entries bury them).
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { HealthIssue, MediaAssetRecord } from '@codot/contracts';
import { mediaApi } from '../api/content.api';
import { since } from '../utils/health';
import SlideOverDrawer from './SlideOverDrawer.vue';

const props = defineProps<{ issue: HealthIssue | null }>();
const emit = defineEmits<{ close: []; open: [assetId: string, name: string] }>();
const { t } = useI18n();

const list = (k: string) => {
  const v = props.issue?.details[k];
  return typeof v === 'string' && v ? v.split(',') : [];
};
const rows = computed(() => list('assetIds').map((id, i) => ({ id, reason: list('reasons')[i] ?? '' })));
const failed = computed(() => Number(props.issue?.details.failed ?? rows.value.length));
const assets = ref(new Map<string, MediaAssetRecord | null>());
const loading = ref(false);

watch(
  () => props.issue?.id,
  async () => {
    assets.value = new Map();
    if (!props.issue || !rows.value.length) return;
    loading.value = true;
    // Deleted items come back as errors: shown by id, nothing to open.
    const got = await Promise.all(rows.value.map((r) => mediaApi.get(r.id).catch(() => null)));
    assets.value = new Map(rows.value.map((r, i) => [r.id, got[i] ?? null]));
    loading.value = false;
  },
  { immediate: true },
);
const why = (r: string) => (r ? t(`health.why_${r}`, r.replace(/_/g, ' ')) : t('notify.whyUnknown'));
</script>

<template>
  <SlideOverDrawer :open="!!issue" :title="t('notify.filesTitle', { n: failed }, failed)" :description="issue?.subjectName" size="md" @close="emit('close')">
    <div v-if="issue" class="space-y-4 text-sm">
      <p class="text-slate-600 dark:text-slate-300">{{ t('notify.filesIntro', { since: since(issue.openedAt) }) }}</p>
      <p v-if="!rows.length" class="rounded-lg border border-slate-200 p-3 text-slate-500 dark:border-slate-800">{{ t('notify.filesUnknown') }}</p>
      <ul v-else class="divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
        <li v-for="r in rows" :key="r.id" class="flex items-center gap-3 p-3">
          <span class="flex h-12 w-20 shrink-0 items-center justify-center overflow-hidden rounded bg-slate-100 dark:bg-slate-800">
            <img v-if="assets.get(r.id)?.thumbnailUrl" :src="assets.get(r.id)!.thumbnailUrl!" alt="" class="h-full w-full object-cover" />
            <span v-else class="text-xs text-slate-400" aria-hidden="true">⚠</span>
          </span>
          <span class="min-w-0 flex-1">
            <span class="block truncate font-medium">{{ assets.get(r.id)?.name ?? (loading ? t('notify.loading') : t('notify.deletedItem')) }}</span>
            <span class="mt-0.5 block text-xs text-red-700 dark:text-red-400">{{ why(r.reason) }}</span>
          </span>
          <button v-if="assets.get(r.id)" type="button" class="shrink-0 text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400" @click="emit('open', r.id, assets.get(r.id)!.name)">
            {{ t('notify.details') }} →
          </button>
        </li>
      </ul>
      <p class="text-xs leading-relaxed text-slate-500">{{ t('notify.filesHint') }}</p>
    </div>
    <template #footer>
      <div class="flex flex-wrap justify-end gap-2">
        <RouterLink v-if="issue" :to="{ name: 'screen-logs', params: { screenId: issue.subjectId } }" class="btn-secondary" @click="emit('close')">{{ t('notify.playerLog') }}</RouterLink>
        <button type="button" class="btn-primary" @click="emit('close')">{{ t('content.close') }}</button>
      </div>
    </template>
  </SlideOverDrawer>
</template>
